import { BlockComponent } from "../../../framework/src/BlockComponent";
import { runEngine } from "../../../framework/src/RunEngine";
import { Message } from "../../../framework/src/Message";
import { IBlock } from "../../../framework/src/IBlock";
import MessageEnum, {
  getName,
} from "../../../framework/src/Messages/MessageEnum";

// Customizable Area Start
import { makeApiMessage as MakeApiMessage } from "../../../components/src/common";

export const configJSON = require("./config");

export interface IOption {
  id: number | string;
  name: string;
  option?: string;
  store_name?: string;
}

export interface ISlotOverride {
  id?: number;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  reason: string;
  activated: boolean;
  _destroy?: boolean;
}

export interface IMobileService {
  id: string;
  type: string;
  attributes: {
    id: number;
    name: string;
    name_ar: string;
    icon_url: string;
    active: boolean;
    position: number;
  };
}

export const SLOT_TYPES = ["pickup", "delivery"];
// Customizable Area End

export interface Props {
  // Customizable Area Start
  navigation: {
    navigate: Function;
    getParam: Function;
  };
  id: string;
  // Customizable Area End
}

export interface S {
  // Customizable Area Start
  isLoading: boolean;
  isSaving: boolean;
  saveStatus: string;

  // Form fields
  mobile_service_id: number | null;
  threshold: string;
  activated: boolean;
  slot_type: string;
  start_time: string;
  end_time: string;
  interval_minutes: string;

  // Dropdown data
  mobileServiceList: IMobileService[];
  regionList: IOption[];
  areaList: IOption[];
  storeList: IOption[];

  // Region multi-select
  regionSelectedData: { id: string; option: string }[];
  selectedRegionsForList: { id: string; option: string }[];
  isRegionSelectAll: boolean;

  // Area multi-select
  areaSelectedData: { id: string; option: string }[];
  selectedAreasForList: { id: string; option: string }[];
  isAreaSelectAll: boolean;

  // Store multi-select
  storeSelectedData: { id: string; option: string }[];
  selectedStoresForList: { id: string; option: string }[];
  isStoreSelectAll: boolean;

  // Slot Overrides
  slotOverrides: ISlotOverride[];

  // Errors
  error: { [key: string]: string };
  // Customizable Area End
}

export interface SS {}

class SlotManagementCreateController extends BlockComponent<Props, S, SS> {
  // Customizable Area Start
  isEdit: boolean = false;
  editId: string = "";
  getMobileServicesApiCallId: string = "";
  getRegionsApiCallId: string = "";
  getAreasApiCallId: string = "";
  getStoresApiCallId: string = "";
  createSlotApiCallId: string = "";
  getSlotDetailApiCallId: string = "";
  tempAreaDataForEdit: any[] = [];
  tempStoreDataForEdit: any[] = [];
  // Customizable Area End

  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    this.subScribedMessages = [
      getName(MessageEnum.RestAPIResponceMessage),
      getName(MessageEnum.RestAPIResponceSuccessMessage),
      getName(MessageEnum.RestAPIResponceDataMessage),
    ];

    this.receive = this.receive.bind(this);
    runEngine.attachBuildingBlock(this as IBlock, this.subScribedMessages);
    // Customizable Area End
  }

  async receive(from: string, message: Message) {
    // Customizable Area Start
    const requestId = message.getData(
      getName(MessageEnum.RestAPIResponceDataMessage)
    );
    const successResponse = message.getData(
      getName(MessageEnum.RestAPIResponceSuccessMessage)
    );

    if (!successResponse) return;

    if (requestId === this.getMobileServicesApiCallId) {
      this.setState({ mobileServiceList: successResponse.data || [] });
    } else if (requestId === this.getRegionsApiCallId) {
      this.handleRegionsResponse(successResponse);
    } else if (requestId === this.getAreasApiCallId) {
      this.handleAreasResponse(successResponse);
    } else if (requestId === this.getStoresApiCallId) {
      this.handleStoresResponse(successResponse);
    } else if (requestId === this.getSlotDetailApiCallId) {
      this.handleSlotDetailResponse(successResponse);
    } else if (requestId === this.createSlotApiCallId) {
      this.setState({ isSaving: false });
      if (successResponse.data) {
        this.setState({ saveStatus: this.isEdit ? "Slot Master updated successfully" : "Slot Master created successfully" });
        setTimeout(() => {
          this.props.navigation.navigate("SlotManagementList");
        }, 1500);
      } else {
        const errorMsg =
          successResponse.errors?.[0] ||
          successResponse.error ||
          "Something went wrong";
        this.setState({
          saveStatus:
            typeof errorMsg === "string"
              ? errorMsg
              : JSON.stringify(errorMsg),
        });
      }
    }
    // Customizable Area End
  }

  // Customizable Area Start
  async componentDidMount() {
    const slotId = this.props.navigation.getParam("id");
    if (slotId) {
      this.isEdit = true;
      this.editId = slotId;
      this.getSlotDetail();
    }
    this.getMobileServices();
    this.getRegions();
  }

  getSlotDetail = () => {
    this.setState({ isLoading: true });
    const requestMessage = MakeApiMessage({
      url: `${configJSON.ApiUrls.slotManagementList}/${this.editId}`,
      method: "GET",
    });
    this.getSlotDetailApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleSlotDetailResponse = (responseJson: any) => {
    this.setState({ isLoading: false });
    if (!responseJson || !responseJson.data) return;

    const item = Array.isArray(responseJson.data)
      ? responseJson.data[0]
      : responseJson.data;
    if (!item) return;

    const attr = item.attributes;

    // Populate region/area/store selections
    const regionData = attr.regions?.map((r: any) => ({
      id: r.id.toString(),
      option: r.name,
    })) || [];
    const areaData = attr.areas?.map((a: any) => ({
      id: a.id.toString(),
      option: a.name,
    })) || [];
    const storeData = attr.stores?.map((s: any) => ({
      id: s.id.toString(),
      option: s.name,
    })) || [];

    // Map slot overrides
    const slotOverrides: ISlotOverride[] = (attr.slot_overrides || []).map((o: any) => ({
      id: o.id,
      start_date: o.start_date || "",
      end_date: o.end_date || "",
      start_time: o.start_time || "",
      end_time: o.end_time || "",
      reason: o.reason || "",
      activated: o.activated,
    }));

    this.setState({
      mobile_service_id: attr.mobile_service?.id || null,
      threshold: String(attr.threshold),
      activated: attr.activated,
      slot_type: attr.slot_type || "pickup",
      start_time: attr.start_time || "",
      end_time: attr.end_time || "",
      interval_minutes:
        attr.interval_minutes === null || attr.interval_minutes === undefined
          ? ""
          : String(attr.interval_minutes),
      regionSelectedData: regionData,
      selectedRegionsForList: regionData,
      areaSelectedData: areaData,
      selectedAreasForList: areaData,
      storeSelectedData: storeData,
      selectedStoresForList: storeData,
      slotOverrides,
    });

    // Load dependent dropdowns
    if (regionData.length > 0) {
      const regionIds = regionData.map((r: any) => Number(r.id));
      this.tempAreaDataForEdit = areaData;
      this.getAreas(regionIds);
    }
    if (areaData.length > 0) {
      const areaIds = areaData.map((a: any) => Number(a.id));
      this.tempStoreDataForEdit = storeData;
      this.getStores(areaIds);
    }
  };

  getMobileServices = () => {
    const requestMessage = MakeApiMessage({
      url: configJSON.ApiUrls.mobileServices,
      method: "GET",
    });
    this.getMobileServicesApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getRegions = () => {
    const requestMessage = MakeApiMessage({
      url: "/bx_block_regions/regions?dropdown=true",
      method: "GET",
    });
    this.getRegionsApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getAreas = (regionIds: number[]) => {
    if (regionIds.length === 0) {
      this.setState({ areaList: [], areaSelectedData: [], selectedAreasForList: [] });
      return;
    }
    const regionQuery = regionIds.map((id) => `region_id[]=${id}`).join("&");
    const requestMessage = MakeApiMessage({
      url: `/bx_block_regions/areas?${regionQuery}&dropdown=true`,
      method: "GET",
    });
    this.getAreasApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  getStores = (areaIds: number[]) => {
    if (areaIds.length === 0) {
      this.setState({ storeList: [], storeSelectedData: [], selectedStoresForList: [] });
      return;
    }
    const areaQuery = areaIds.map((id) => `area_id[]=${id}`).join("&");
    const requestMessage = MakeApiMessage({
      url: `/bx_block_store_management/store_managements?${areaQuery}&dropdown=true&allow_access=true`,
      method: "GET",
    });
    this.getStoresApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  handleRegionsResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const regionList = responseJson.data.map((item: any) => ({
        id: item.id,
        name:
          item.attributes.region_name ||
          item.attributes.name ||
          item.attributes.branch_name ||
          item.attributes.name_translation,
        option:
          item.attributes.region_name ||
          item.attributes.name ||
          item.attributes.branch_name ||
          item.attributes.name_translation,
      }));
      this.setState({ regionList });
    }
  };

  handleAreasResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const areaList = responseJson.data.map((item: any) => ({
        id: item.id,
        name:
          item.attributes.area_name ||
          item.attributes.name ||
          item.attributes.name_translation,
        option:
          item.attributes.area_name ||
          item.attributes.name ||
          item.attributes.name_translation,
      }));

      const shouldRestore = this.isEdit && this.tempAreaDataForEdit.length > 0;
      const areaDataToUse = shouldRestore ? this.tempAreaDataForEdit : [];

      this.setState({
        areaList,
        areaSelectedData: shouldRestore ? areaDataToUse : this.state.areaSelectedData,
        selectedAreasForList: shouldRestore ? areaDataToUse : this.state.selectedAreasForList,
      });

      if (shouldRestore) {
        this.tempAreaDataForEdit = [];
      }
    }
  };

  handleStoresResponse = (responseJson: any) => {
    if (responseJson && responseJson.data) {
      const storeList = responseJson.data.map((item: any) => ({
        id: item.id,
        name: item.attributes.store_name || item.attributes.name,
        option: item.attributes.store_name || item.attributes.name,
        store_name: item.attributes.store_name,
      }));

      const shouldRestore = this.isEdit && this.tempStoreDataForEdit.length > 0;
      const storeDataToUse = shouldRestore ? this.tempStoreDataForEdit : [];

      this.setState({
        storeList,
        storeSelectedData: shouldRestore ? storeDataToUse : this.state.storeSelectedData,
        selectedStoresForList: shouldRestore ? storeDataToUse : this.state.selectedStoresForList,
      });

      if (shouldRestore) {
        this.tempStoreDataForEdit = [];
      }
    }
  };

  // Multi-select helpers
  updateMultiSelectData = (
    newItems: { id: string | number; option: string }[],
    totalList: { id: string | number; option: string }[],
    isSelectAll: string,
    selectedData: string,
    selectedForList: string
  ) => {
    const updateStateData: {
      [key: string]: boolean | { id: string | number; option: string }[];
    } = {};
    updateStateData[isSelectAll] = false;
    updateStateData[selectedData] = newItems;
    updateStateData[selectedForList] = newItems;
    if (newItems.length === totalList.length) {
      updateStateData[isSelectAll] = true;
      updateStateData[selectedData] = [{ id: "-1", option: "Select All" }];
      updateStateData[selectedForList] = [
        { id: "-1", option: "Select All" },
        ...totalList,
      ];
    }
    return updateStateData;
  };

  handleRegionMultiSelect = (
    value: { id: string; option: string }[],
    option: { id: string; option: string }
  ) => {
    let updateStateData: Partial<S> = {};
    if (option.id == "-1") {
      updateStateData = this.state.isRegionSelectAll
        ? {
            isRegionSelectAll: false,
            regionSelectedData: [],
            selectedRegionsForList: [],
            areaList: [],
            areaSelectedData: [],
            selectedAreasForList: [],
          }
        : {
            isRegionSelectAll: true,
            regionSelectedData: [{ id: "-1", option: "Select All" }],
            selectedRegionsForList: [
              { id: "-1", option: "Select All" },
              ...this.state.regionList.map((item: any) => ({
                id: item.id,
                option: item.option || item.name,
              })),
            ],
            areaSelectedData: [],
            selectedAreasForList: [],
          };
    } else {
      const isValueContained = value.some((item) => item.id == option.id);
      value = value.filter((item) => item.id !== "-1");
      const newItems = isValueContained
        ? value.filter((item) => item.id != option.id)
        : [...value, option];
      const regionUpdateData = this.updateMultiSelectData(
        newItems,
        this.state.regionList.map((item: any) => ({
          id: item.id,
          option: item.option || item.name,
        })),
        "isRegionSelectAll",
        "regionSelectedData",
        "selectedRegionsForList"
      );
      updateStateData = {
        ...regionUpdateData,
        areaSelectedData: [],
        selectedAreasForList: [],
      } as Partial<S>;
    }
    this.setState(updateStateData as Pick<S, keyof S>, () => {
      let regionIdsForAPI: number[];
      if (this.state.isRegionSelectAll) {
        regionIdsForAPI = this.state.regionList.map((item: any) =>
          Number(item.id)
        );
      } else {
        regionIdsForAPI =
          this.state.regionSelectedData
            ?.filter((item: any) => item.id !== "All" && item.id !== "-1")
            .map((item: any) => Number(item.id)) || [];
      }
      if (regionIdsForAPI.length > 0) {
        this.getAreas(regionIdsForAPI);
      } else {
        this.setState({
          areaList: [],
          areaSelectedData: [],
          selectedAreasForList: [],
        });
      }
    });
  };

  handleAreaMultiSelect = (
    value: { id: string; option: string }[],
    option: { id: string; option: string }
  ) => {
    let updateStateData: Partial<S> = {};
    if (option.id == "-1") {
      updateStateData = this.state.isAreaSelectAll
        ? {
            isAreaSelectAll: false,
            areaSelectedData: [],
            selectedAreasForList: [],
          }
        : {
            isAreaSelectAll: true,
            areaSelectedData: [{ id: "-1", option: "Select All" }],
            selectedAreasForList: [
              { id: "-1", option: "Select All" },
              ...this.state.areaList.map((item: any) => ({
                id: item.id,
                option: item.option || item.name,
              })),
            ],
          };
    } else {
      const isValueContained = value.some((item) => item.id == option.id);
      value = value.filter((item) => item.id !== "-1");
      const newItems = isValueContained
        ? value.filter((item) => item.id != option.id)
        : [...value, option];
      const areaUpdateData = this.updateMultiSelectData(
        newItems,
        this.state.areaList.map((item: any) => ({
          id: item.id,
          option: item.option || item.name,
        })),
        "isAreaSelectAll",
        "areaSelectedData",
        "selectedAreasForList"
      );
      updateStateData = { ...areaUpdateData } as Partial<S>;
    }
    this.setState(updateStateData as Pick<S, keyof S>, () => {
      let areaIdsForAPI: number[];
      if (this.state.isAreaSelectAll) {
        areaIdsForAPI = this.state.areaList.map((item: any) =>
          Number(item.id)
        );
      } else {
        areaIdsForAPI =
          this.state.areaSelectedData
            ?.filter((item: any) => item.id !== "All" && item.id !== "-1")
            .map((item: any) => Number(item.id)) || [];
      }
      if (areaIdsForAPI.length > 0) {
        this.getStores(areaIdsForAPI);
      } else {
        this.setState({
          storeList: [],
          storeSelectedData: [],
          selectedStoresForList: [],
        });
      }
    });
  };

  handleStoreMultiSelect = (
    value: { id: string; option: string }[],
    option: { id: string; option: string }
  ) => {
    let updateStateData: Partial<S> = {};
    if (option.id == "-1") {
      updateStateData = this.state.isStoreSelectAll
        ? {
            isStoreSelectAll: false,
            storeSelectedData: [],
            selectedStoresForList: [],
          }
        : {
            isStoreSelectAll: true,
            storeSelectedData: [{ id: "-1", option: "Select All" }],
            selectedStoresForList: [
              { id: "-1", option: "Select All" },
              ...this.state.storeList.map((item: any) => ({
                id: item.id,
                option: item.option || item.name,
              })),
            ],
          };
    } else {
      const isValueContained = value.some((item) => item.id == option.id);
      value = value.filter((item) => item.id !== "-1");
      const newItems = isValueContained
        ? value.filter((item) => item.id != option.id)
        : [...value, option];
      const storeUpdateData = this.updateMultiSelectData(
        newItems,
        this.state.storeList.map((item: any) => ({
          id: item.id,
          option: item.option || item.name,
        })),
        "isStoreSelectAll",
        "storeSelectedData",
        "selectedStoresForList"
      );
      updateStateData = { ...storeUpdateData } as Partial<S>;
    }
    this.setState(updateStateData as Pick<S, keyof S>);
  };

  // Window configuration. Slots are generated by the backend from these three
  // fields and apply to every day, so there is nothing per-weekday to edit.
  updateWindowField = (
    field: "slot_type" | "start_time" | "end_time" | "interval_minutes",
    value: string
  ) => {
    this.setState({ ...this.state, [field]: value } as Pick<S, keyof S>);
  };

  // Mirrors SlotMaster#slot_windows: a trailing remainder that cannot fit a whole
  // interval is dropped rather than offered as a short slot.
  getGeneratedWindows = (): { start: string; end: string }[] => {
    const { start_time, end_time, interval_minutes } = this.state;
    const step = Number(interval_minutes);
    if (!start_time || !end_time || !step || step <= 0) return [];

    const toMinutes = (value: string) => {
      const [hours, minutes] = value.split(":").map(Number);
      if (isNaN(hours) || isNaN(minutes)) return NaN;
      return hours * 60 + minutes;
    };
    const format = (total: number) =>
      `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(
        total % 60
      ).padStart(2, "0")}`;

    let cursor = toMinutes(start_time);
    const finish = toMinutes(end_time);
    if (isNaN(cursor) || isNaN(finish)) return [];

    const windows: { start: string; end: string }[] = [];
    while (cursor + step <= finish) {
      windows.push({ start: format(cursor), end: format(cursor + step) });
      cursor += step;
    }
    return windows;
  };

  getSlotsPerDay = () => this.getGeneratedWindows().length;

  // threshold is the daily booking allowance spread evenly across the day's
  // slots. Flat per slot — independent of how many drivers are on shift.
  getPerSlotCapacity = () => {
    const count = this.getSlotsPerDay();
    if (!count) return 0;
    return Math.floor(Number(this.state.threshold || 0) / count);
  };

  // Slot Override management
  addSlotOverride = () => {
    const newOverride: ISlotOverride = {
      start_date: "",
      end_date: "",
      start_time: "",
      end_time: "",
      reason: "",
      activated: true,
    };
    this.setState({ slotOverrides: [...this.state.slotOverrides, newOverride] });
  };

  removeSlotOverride = (index: number) => {
    const slotOverrides = [...this.state.slotOverrides];
    if (slotOverrides[index].id) {
      slotOverrides[index] = { ...slotOverrides[index], _destroy: true };
    } else {
      slotOverrides.splice(index, 1);
    }
    this.setState({ slotOverrides });
  };

  updateSlotOverride = (index: number, field: keyof ISlotOverride, value: any) => {
    const slotOverrides = [...this.state.slotOverrides];
    slotOverrides[index] = { ...slotOverrides[index], [field]: value };
    this.setState({ slotOverrides });
  };

  // Validation
  validate = (): boolean => {
    const error: { [key: string]: string } = {};

    if (!this.state.mobile_service_id) {
      error.mobile_service_id = "Mobile Service is required";
    }
    if (
      !this.state.threshold ||
      isNaN(Number(this.state.threshold)) ||
      Number(this.state.threshold) <= 0
    ) {
      error.threshold = "Threshold must be a positive number";
    }

    // Validate the window. Mirrors SlotMaster#validate_slot_window so the user is
    // told here rather than by a 422.
    if (!this.state.slot_type) {
      error.slot_type = "Slot type is required";
    }
    if (!this.state.start_time) {
      error.start_time = "Start time is required";
    }
    if (!this.state.end_time) {
      error.end_time = "End time is required";
    }
    if (
      this.state.start_time &&
      this.state.end_time &&
      this.state.end_time <= this.state.start_time
    ) {
      error.end_time = "End time must be after start time";
    }
    if (
      !this.state.interval_minutes ||
      isNaN(Number(this.state.interval_minutes)) ||
      Number(this.state.interval_minutes) <= 0
    ) {
      error.interval_minutes = "Interval must be a positive number";
    } else if (
      this.state.start_time &&
      this.state.end_time &&
      this.state.end_time > this.state.start_time &&
      this.getSlotsPerDay() === 0
    ) {
      error.interval_minutes =
        "Interval is longer than the window between start and end time";
    }

    // Validate slot overrides
    const activeOverrides = this.state.slotOverrides.filter((o) => !o._destroy);
    for (let i = 0; i < activeOverrides.length; i++) {
      const override = activeOverrides[i];
      if (!override.start_date) {
        error[`override_${i}_start_date`] = "Start date is required";
      }
      if (!override.end_date) {
        error[`override_${i}_end_date`] = "End date is required";
      }
      if (override.start_date && override.end_date && override.start_date > override.end_date) {
        error[`override_${i}_end_date`] = "End date must be after start date";
      }
      if (!override.reason) {
        error[`override_${i}_reason`] = "Reason is required";
      }
    }

    this.setState({ error });
    return Object.keys(error).length === 0;
  };

  // Submit
  onSubmit = () => {
    if (!this.validate()) return;

    this.setState({ isSaving: true, saveStatus: "" });

    const regionIds = this.state.isRegionSelectAll
      ? this.state.regionList.map((r: any) => Number(r.id))
      : this.state.regionSelectedData
          .filter((r) => r.id !== "All" && r.id !== "-1")
          .map((r) => Number(r.id));

    const areaIds = this.state.isAreaSelectAll
      ? this.state.areaList.map((a: any) => Number(a.id))
      : this.state.areaSelectedData
          .filter((a) => a.id !== "All" && a.id !== "-1")
          .map((a) => Number(a.id));

    const storeIds = this.state.isStoreSelectAll
      ? this.state.storeList.map((s: any) => Number(s.id))
      : this.state.storeSelectedData
          .filter((s) => s.id !== "All" && s.id !== "-1")
          .map((s) => Number(s.id));

    const slotOverridesAttributes = this.state.slotOverrides.map((o) => {
      const override: any = {
        start_date: o.start_date,
        end_date: o.end_date,
        start_time: o.start_time || null,
        end_time: o.end_time || null,
        reason: o.reason,
        activated: o.activated,
      };
      if (o.id) override.id = o.id;
      if (o._destroy) override._destroy = true;
      return override;
    });

    const body = {
      data: {
        mobile_service_id: this.state.mobile_service_id,
        threshold: Number(this.state.threshold),
        activated: this.state.activated,
        slot_type: this.state.slot_type,
        start_time: this.state.start_time,
        end_time: this.state.end_time,
        interval_minutes: Number(this.state.interval_minutes),
        region_ids: regionIds,
        area_ids: areaIds,
        store_ids: storeIds,
        // slots_attributes is deliberately not sent: the backend regenerates the
        // Slot rows from the window on every save, so posting them would conflict.
        slot_overrides_attributes: slotOverridesAttributes.filter((o: any) => !o._destroy || o.id),
      },
    };

    let requestMessage;
    if (this.isEdit) {
      requestMessage = MakeApiMessage({
        url: `${configJSON.ApiUrls.slotManagementList}/${this.editId}`,
        method: "PUT",
        body: JSON.stringify(body),
      });
    } else {
      requestMessage = MakeApiMessage({
        url: configJSON.ApiUrls.slotManagementList,
        method: "POST",
        body: JSON.stringify(body),
      });
    }

    this.createSlotApiCallId = requestMessage.messageId;
    runEngine.sendMessage(requestMessage.id, requestMessage);
  };

  clickCancel = () => {
    this.props.navigation.navigate("SlotManagementList");
  };
  // Customizable Area End
}

export default SlotManagementCreateController;
